import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import AccessTimeIcon from '@material-ui/icons/AccessTime';

import PickerField from '../PickerField';
import Panel from '../PickerField/Panel';
import Stepper from '../PickerField/Stepper';
import useStyles from '../PickerField/useStyles';

const LABELS = {
  title: '',
  hours: 'Hours',
  minutes: 'Minutes',
  hourUnit: 'h',
  minuteUnit: 'm',
  quick: 'Quick set duration',
  quickUnit: 'min',
  cancel: 'Cancel',
  apply: 'Apply',
  increase: 'Increase',
  decrease: 'Decrease',
};

// Duration as `{ hours, minutes }`, hours within `minHours`–`maxHours` and minutes on `minuteStep`.
const DurationField = ({
  value,
  onChange,
  minHours,
  maxHours,
  minuteStep,
  quickSet,
  labels,
  disabled,
  error,
  className,
}) => {
  const classes = useStyles();
  const anchor = useRef(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const text = { ...LABELS, ...labels };
  const hours = { min: minHours, max: maxHours };
  const minutes = { min: 0, max: 60 - minuteStep, step: minuteStep };

  const isAllowed = (total) =>
    total >= minHours * 60 && total < (maxHours + 1) * 60 && (total % 60) % minuteStep === 0;

  return (
    <>
      <PickerField
        ref={anchor}
        icon={<AccessTimeIcon />}
        open={open}
        error={error}
        disabled={disabled}
        className={className}
        onClick={() => {
          setDraft(value);
          setOpen(true);
        }}
      >
        <span className={classnames(classes.part, classes.partSpaced)}>
          {value.hours}
          <span className={classes.unit}>{text.hourUnit}</span>
        </span>
        <span className={classes.part}>
          {value.minutes}
          <span className={classes.unit}>{text.minuteUnit}</span>
        </span>
      </PickerField>
      <Panel
        anchorEl={anchor.current}
        open={open}
        labels={text}
        chips={quickSet.filter(isAllowed).map((total) => ({
          label: `${total} ${text.quickUnit}`,
          onClick: () => setDraft({ hours: Math.floor(total / 60), minutes: total % 60 }),
        }))}
        onClose={() => setOpen(false)}
        onApply={() => {
          onChange(draft);
          setOpen(false);
        }}
      >
        <Stepper
          label={text.hours}
          labels={text}
          value={draft.hours}
          onChange={(next) => setDraft({ ...draft, hours: next })}
          {...hours}
        />
        <Stepper
          label={text.minutes}
          labels={text}
          value={draft.minutes}
          onChange={(next) => setDraft({ ...draft, minutes: next })}
          {...minutes}
        />
      </Panel>
    </>
  );
};

DurationField.propTypes = {
  value: PropTypes.shape({
    hours: PropTypes.number.isRequired,
    minutes: PropTypes.number.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  minHours: PropTypes.number,
  maxHours: PropTypes.number.isRequired,
  minuteStep: PropTypes.number,
  quickSet: PropTypes.arrayOf(PropTypes.number),
  labels: PropTypes.objectOf(PropTypes.string),
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  className: PropTypes.string,
};

DurationField.defaultProps = {
  minHours: 0,
  minuteStep: 1,
  quickSet: [15, 30, 45],
  labels: LABELS,
  disabled: false,
  error: false,
  className: undefined,
};

export default DurationField;
